/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { lazy, Suspense, useState } from 'react';
import { Blend, ChevronDown, LogOut, ShieldCheck, Tag } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useCircles, useMe } from '@/queries/session';
import { initials, ownerColour } from '@/lib/avatar';
import { Tip } from './Tip';
import { ThemeSwitch } from './ThemeSwitch';
import { PushRow } from './PushRow';
import { signOutCleanup } from './push';

/**
 * The three screens behind this menu, fetched on demand.
 *
 * Together they are around 900 lines that most people never open and that an
 * administrator opens perhaps once a week, and they were in the chunk everybody
 * downloads before the board could paint.
 *
 * **The fetch is started when the menu opens, not when an item is clicked.**
 * That is the whole reason this does not feel like a downgrade: the gap between
 * "the menu appeared" and "you chose a row" is comfortably longer than the
 * fetch, so the screen is already in memory by the time it is asked for.
 */
const SCREENS = {
  circles: () => import('@/admin/CircleManager'),
  admins: () => import('@/admin/AdminManager'),
  tags: () => import('@/task/TagManager'),
};

const CircleManager = lazy(() => SCREENS.circles().then((m) => ({ default: m.CircleManager })));
const AdminManager = lazy(() => SCREENS.admins().then((m) => ({ default: m.AdminManager })));
const TagManager = lazy(() => SCREENS.tags().then((m) => ({ default: m.TagManager })));

type Screen = keyof typeof SCREENS;

/**
 * Who you are, and the screens that are not views.
 *
 * **Manage circles is not rendered for somebody who is not an administrator** —
 * `admin-tiers-view` asserts it is absent, not disabled. A control that answers
 * 403 is worse than no control: it tells somebody a screen exists and then
 * refuses it, which reads as a fault rather than a boundary.
 *
 * The menu is presentation only. `requireAdmin` on the server is the boundary,
 * and hiding an item has never been what stops anybody.
 *
 * Manage tags is here as well as inside the tag picker, because tidying tags is
 * something you set out to do rather than something you discover mid-task.
 */
export function AccountMenu() {
  const { data: me } = useMe();
  const { data: circles } = useCircles();
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState<Screen | null>(null);
  // Which screens have ever been opened. A lazy component only fetches once it
  // is rendered, so mounting all three unconditionally would defeat the split —
  // but unmounting one the moment it closes would cut its closing animation off
  // at the first frame. Latching gives both: nothing is fetched until it is
  // wanted, and once mounted it stays to animate out.
  const [everOpened, setEverOpened] = useState<Partial<Record<Screen, boolean>>>({});

  // The same two functions the cards and the admin rows use, so one person is
  // one pair of letters and one colour everywhere in the app — vanilla derives
  // this button from `currentUser.name || currentUser.email` the same way.
  const who = me?.name || me?.email || '?';

  const go = (to: Screen) => {
    setOpen(false);
    setScreen(to);
    setEverOpened((s) => (s[to] ? s : { ...s, [to]: true }));
  };

  // Opening the menu is the signal to fetch what it can lead to. Rejections are
  // ignored: the lazy component will ask again, and a failed prefetch should not
  // reach anybody.
  const openMenu = (next: boolean) => {
    setOpen(next);
    if (next) for (const get of Object.values(SCREENS)) void get().catch(() => {});
  };

  return (
    <>
      <Popover.Root open={open} onOpenChange={openMenu}>
        {/* Names the person, which two initials cannot. It replaced a `title`
            doing the same job a second slower — and leaving both would fire two
            tooltips over each other. */}
        <Tip label={who}>
          <Popover.Trigger asChild>
            <button
              id="user-btn"
              aria-label="Account"
              className="user-btn tap shrink-0 transition-colors duration-fast ease-out-soft"
            >
              {/* The coloured avatar and a caret, as vanilla draws it. A plain
                  bordered circle with initials in it reads as a status chip; the
                  caret is the only thing saying a menu opens here — and the
                  colour is the same one this person has on every card. */}
              <span className="avatar" style={{ background: ownerColour(who) }}>
                {initials(who)}
              </span>
              <ChevronDown className="caret" aria-hidden />
            </button>
          </Popover.Trigger>
        </Tip>

        <Popover.Portal>
          <Popover.Content
            id="user-menu"
            align="end"
            sideOffset={8}
            className="motion-surface open z-50 min-w-56 rounded-xl border border-border bg-surface p-1.5 shadow-overlay"
          >
            <div className="border-b border-border px-2.5 pb-2 pt-1">
              <p className="m-0 truncate text-sm font-semibold">{me?.name ?? me?.email}</p>
              {me?.name && <p className="m-0 truncate text-2xs text-ink-faint">{me.email}</p>}
            </div>

            {/* **Its own group, above the Manage items — not among them.**
                Manage tags, circles and admins are rendered only for
                administrators; an ordinary person's menu is their name,
                Appearance and Logout. Putting push in that group would hide it
                from almost everybody it was built for. */}
            <PushRow />

            <MenuItem icon={ICONS.tag} onClick={() => go('tags')}>Manage tags</MenuItem>

            {/* Absent, not disabled, for anybody who is not an administrator. */}
            {circles?.isAdmin && (
              <MenuItem icon={ICONS.circles} onClick={() => go('circles')}>Manage circles</MenuItem>
            )}
            {circles?.isAdmin && (
              <MenuItem icon={ICONS.shield} onClick={() => go('admins')}>Manage admins</MenuItem>
            )}

            {/* Above Logout, because Logout is the one row you must never hit by
                accident and it earns being last. */}
            <ThemeSwitch />

            <div className="my-1 border-t border-border" />
            {/* A real link, not a fetch: signing out clears a cookie the server
                set and then sends you to the login page. Doing it with fetch
                would leave the browser sitting on a board it can no longer
                load. */}
            <a
              href="/auth/logout"
              onClick={(e) => {
                /**
                 * **Unsubscribe this device before leaving.**
                 *
                 * A push carries a circle name and a task title, and a phone
                 * screen is not an inbox — it must not keep arriving on a device
                 * somebody has signed out of (plan §2). It has to happen here
                 * rather than in `/auth/logout`, because the server has no way to
                 * know *which* device is leaving: the session cookie says who,
                 * never what they are holding.
                 *
                 * The navigation is deferred rather than cancelled — the href
                 * stays real, so a middle-click or "open in new tab" still signs
                 * out, and `signOutCleanup` swallows everything, so a failure to
                 * tidy can never trap somebody in a session they are trying to
                 * leave.
                 */
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                void signOutCleanup().finally(() => {
                  window.location.href = '/auth/logout';
                });
              }}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-ink no-underline transition-colors duration-fast ease-out-soft hover:bg-bg [&>svg]:size-[15px] [&>svg]:shrink-0 [&>svg]:text-ink-faint"
            >
              {ICONS.logout}
              {/* "Logout", which is what the app production serves says. The
                  rewrite called it "Sign out" — better English, and a rename
                  nobody asked for on the one control a person has to find when
                  they are already leaving. */}
              Logout
            </a>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* No visible fallback: each of these is a dialog that draws its own
          backdrop, and a spinner in the page behind it would be a second thing
          appearing. By this point the chunk is almost always already here — the
          fetch started when the menu opened. */}
      <Suspense fallback={null}>
        {everOpened.circles && (
          <CircleManager open={screen === 'circles'} onOpenChange={(o) => !o && setScreen(null)} />
        )}
        {everOpened.admins && (
          <AdminManager open={screen === 'admins'} onOpenChange={(o) => !o && setScreen(null)} />
        )}
        {everOpened.tags && (
          <TagManager open={screen === 'tags'} onOpenChange={(o) => !o && setScreen(null)} />
        )}
      </Suspense>
    </>
  );
}

/**
 * The four icons this menu has always had — in the app production serves.
 *
 * The rewrite dropped them and printed four bare words in muted grey, and every
 * check stayed green: each item was present, under the right label, doing the
 * right thing. Nothing here measures whether a row has a picture beside it, and
 * a menu of unadorned text reads as a debug list rather than a product.
 *
 * The paths are copied from `renderUserMenu()` in `public/app.js`. Keep them the
 * same shape if either side changes: the tag, the two overlapping circles, the
 * shield with a tick, and the arrow leaving a door.
 */
const ICONS = {
  tag: <Tag aria-hidden />,
  // Two overlapping circles, which is what a circle *is* in this product and
  // what vanilla draws. `Users` was the other candidate and says something
  // subtly different — a circle here is a scope, not a crowd.
  circles: <Blend aria-hidden />,
  shield: <ShieldCheck aria-hidden />,
  logout: <LogOut aria-hidden />,
};

function MenuItem({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      // `font-semibold text-ink` with a faint icon, which is what
      // `.user-menu-item` has always been. The rewrite had these at muted weight
      // and muted colour, so the menu read as four disabled rows.
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-ink transition-colors duration-fast ease-out-soft hover:bg-bg [&>svg]:size-[15px] [&>svg]:shrink-0 [&>svg]:text-ink-faint"
    >
      {icon}
      {children}
    </button>
  );
}
